import { memo } from "preact/compat";
import { useTranslation } from "react-i18next";
import { BetAvatar, Typography } from "../../../shared/ui";
import { ChatRecord } from "../model/types";
import css from "./ChatMessageItem.module.scss";

/** Должен совпадать с `$avatar-size` в модуле стилей: от него считается отступ бабла. */
const AVATAR_SIZE = 34;

function formatTime(epochMs: number): string {
  if (!epochMs) return "";
  const date = new Date(epochMs);
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
}

interface ChatMessageItemProps {
  record: ChatRecord;
  isOwn?: boolean;
}

/** Двойная галочка «доставлено» — только для своих сообщений. */
function DeliveredIcon() {
  return (
    <svg
      className={css.delivered}
      width="14"
      height="10"
      viewBox="0 0 14 10"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M1 5.4L3.6 8L8.6 2"
        stroke="currentColor"
        stroke-width="1.2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M6.2 8L11.2 2"
        stroke="currentColor"
        stroke-width="1.2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
}

function ChatMessageItemComponent({ record, isOwn }: ChatMessageItemProps) {
  const { t } = useTranslation();

  // Tombstone — terminal state. Content не восстанавливается (§10).
  if (record.deleted) {
    return (
      <div className={`${css.row} ${isOwn ? css.own : ""}`}>
        {!isOwn && <div className={css.avatarColumn} />}
        <div className={css.messageColumn}>
          <div className={`${css.bubble} ${css.tombstone}`}>
            <Typography variant="caption3" color="grey">
              {t("chat.deleted")}
            </Typography>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${css.row} ${isOwn ? css.own : ""}`}>
      {!isOwn && (
        <div className={css.avatarColumn}>
          <BetAvatar avatar={`${record.avatar}.png`} size={AVATAR_SIZE} />
        </div>
      )}

      <div className={css.messageColumn}>
        {!isOwn && (
          <div className={css.name}>
            <Typography variant="caption2" color="light-grey">
              {record.playerName}
            </Typography>
          </div>
        )}

        <div className={css.bubble}>
          {/* Текст рендерится как text, не как markup (§13). */}
          <Typography
            variant="caption2"
            color={isOwn ? "white" : "black"}
            className={css.text}
          >
            {record.text}
          </Typography>

          <div className={css.footer}>
            <Typography
              variant="caption3"
              color={isOwn ? "white" : "light-grey"}
              className={css.time}
            >
              {formatTime(record.sentAt)}
            </Typography>
            {isOwn && <DeliveredIcon />}
          </div>
        </div>
      </div>
    </div>
  );
}

export const ChatMessageItem = memo(ChatMessageItemComponent);
